// React
import React, { useState, useMemo, useEffect, useCallback } from 'react';

// Material UI
import Box from '@material-ui/core/Box';
import TableContainer from '@material-ui/core/TableContainer';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import TablePagination from '@material-ui/core/TablePagination';
import DeleteIcon from '@material-ui/icons/Delete';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';

// Utils
import request, { useFetch } from '../../../utils/request';
import clsx from 'clsx';
import moment from 'moment';
import { NotificationManager } from 'react-notifications';

// Constants
import { BACKEND_URL, DATE_TIME_FORMAT } from '../../../constants';

// Components
import OverlayLoader from '../../Loader/OverlayLoader';
import TablePaginationActions from '../../TablePaginationActions';
import ConfirmationModal from '../../ConfirmationDialog';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    maxWidth: 800,
    width: '100%'
  },
  tableContainer: {
    width: '100%'
  },
  table: {
    minWidth: 400
  },
  cell: {
    padding: theme.table.cellPadding,
    wordBreak: 'break-all'
  },
  headCell: {
    backgroundColor: theme.table.headerBackground,
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    whiteSpace: 'nowrap'
  },
  paginationToolbar: {
    overflow: 'hidden',
    minHeight: 52
  },
  dateCell: {
    width: 200
  },
  smallCell: {
    width: 60,
    textAlign: 'center',
    padding: theme.table.smCellPadding
  }
}));

const RaffleHistory = ({ loadCount }) => {
  const classes = useStyles();

  const [confirmDeleteProps, setConfirmDeleteProps] = useState(null);

  const [refresh, setRefresh] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(+localStorage.getItem('rowsPerPage') || 10);
  const [page, setPage] = useState(1);

  const { loading, data } = useFetch(
    `${BACKEND_URL}/promotions/winners?page=${page}&limit=${rowsPerPage}&s_created_at=desc`,
    null,
    refresh
  );

  useEffect(() => {
    setPage(1);
    setRefresh((oldValue) => !oldValue);
  }, [loadCount, setPage, setRefresh]);

  const columns = useMemo(
    () => [
      { id: 'phone', label: 'Client' },
      {
        id: 'created_at',
        label: 'Dată extragere',
        cls: classes.dateCell,
        format: (date) => moment(date).format(DATE_TIME_FORMAT)
      }
    ],
    [classes]
  );

  const handleChangePage = useCallback(
    (_, newPage) => {
      setPage(newPage + 1);
    },
    [setPage]
  );

  const handleChangeRowsPerPage = useCallback(
    (event) => {
      localStorage.setItem('rowsPerPage', +event.target.value);
      setRowsPerPage(+event.target.value);
      setPage(1);
    },
    [setRowsPerPage, setPage]
  );

  const deleteEntry = useCallback(
    async (id) => {
      try {
        await request(`${BACKEND_URL}/promotions/winners/${id}`, {
          method: 'DELETE'
        });

        // Reset page if there are no results and the page is not the first one
        if (data.data.length === 1 && page > 1) {
          setPage(1);
        } else {
          setRefresh((oldValue) => !oldValue);
        }

        NotificationManager.success(`Șters cu succes!`);
      } catch (ex) {
        NotificationManager.error(`A apărut o eroare: ${ex.message}`);
      }
    },
    [setRefresh, data]
  );

  return (
    <Box classes={{ root: classes.wrapper }}>
      {loading && <OverlayLoader />}

      <TableContainer className={classes.tableContainer}>
        <Table stickyHeader classes={{ root: classes.table }}>
          <TableHead>
            <TableRow>
              {columns.map(({ id, cls, label, format, children, ...rest }) => (
                <TableCell key={id} classes={{ root: clsx(classes.cell, cls), head: classes.headCell }} {...rest}>
                  {children || label}
                </TableCell>
              ))}

              <TableCell classes={{ root: clsx(classes.cell, classes.smallCell), head: classes.headCell }}>
                Ștergere
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data &&
              data.data.map((logData) => (
                <TableRow key={logData.id}>
                  {columns.map(({ id, format, cls }) => {
                    const value = logData[id];

                    return (
                      <TableCell key={id} classes={{ root: clsx(classes.cell, cls) }}>
                        {format ? format(value, logData) : value || '-'}
                      </TableCell>
                    );
                  })}

                  <TableCell classes={{ root: clsx(classes.cell, classes.smallCell) }}>
                    <IconButton
                      onClick={() =>
                        setConfirmDeleteProps({
                          confirmCallback: () => deleteEntry(logData.id),
                          message: `Ești sigur?`
                        })
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Rânduri pe pagină:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to === -1 ? count : to} din ${count}`}
        classes={{ root: classes.paginationToolbar }}
        component="div"
        count={data ? data.total : 0}
        rowsPerPage={rowsPerPage}
        page={data ? page - 1 : 0}
        onChangePage={handleChangePage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        ActionsComponent={TablePaginationActions}
      />

      {confirmDeleteProps && <ConfirmationModal onClose={() => setConfirmDeleteProps(null)} {...confirmDeleteProps} />}
    </Box>
  );
};

export default RaffleHistory;
